import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { useGetRelatedDirects } from '../api/getRelatedDirects';

import { RelatedDirectsListItem } from './RelatedDirectsListItem';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { Search } from 'components/Search';
import { useDebouncedCallback } from 'use-debounce';
import { NotFound } from 'components/NotFound/NotFound';
import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { CheckboxFilter } from 'components/Filter/Inputs/CheckboxFilter';
import { PremiumBanner } from '../../subscriptions/components/PremiumBanner';

export const DirectsTab = ({ id, currentTab, activeCount, expiredCount, companyType }) => {
  const directsTabName = 'directs';
  const includeExpired = companyType === "buyer" ? false : true;
  const { account, user } = useAuth();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    includeInactive: false,
    acceptingOffers: account.super_admin ? false : true,
    sortBy: 'dateCreated',
  });
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const labelName = "Directs";
  const labelNameSingular = "Direct";

  const {
    data: { directs = [], total } = {},
    refetch: refetchDirects,
    isFetching,
  } = useGetRelatedDirects(id, {
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: (currentTab === directsTabName && (account.super_admin || companyType === 'buyer' || (user.Company.is_premium && user.Company.subscription_level !== 'free')))
    },
  });

  const handlePageChange = (_, page) => setPage(page);
  const handleFilterUpdate = ({ target: { name, checked } }) => {
    setFilters((filters) => {
      return { ...filters, [name]: checked };
    });
  };
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useTabReset();
  useRefetchDirects();

  const additionalSilver = activeCount - total;
  const additionalGold = additionalSilver + expiredCount;

  let messageGold = `There are ${additionalGold} more ${labelName} available for Premium members`;
  if (additionalGold === 1) {
    messageGold = `There is ${additionalGold} more ${labelNameSingular} available for Premium members`
  }

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  if (currentTab !== directsTabName) return null;

  return (
    <>
      <Stack gap={2} height={1}>
        {!account.super_admin && (
          <>
          {companyType === 'buyer' && (
            <>
              {(!user.Company.is_premium || user.Company.subscription_level === 'free') && (
                <PremiumBanner sx={{mt:-2, mb:0, ml: -4, width: { xs: 768, lg: 900, xl: 1200 }}} message={`${messageGold}. Click here for more information`} handleSuccess={refetchDirects} currentLevel={user.Company.subscription_level} />
              )}
            </>
          )}
          {companyType === 'supplier' && (!user.Company.is_premium || user.Company.subscription_level === 'free') && (
            <PremiumBanner sx={{mt:-2, mb:0, ml: -4, width: { xs: 768, lg: 900, xl: 1200 }}} message={`${messageGold}. Click here for more information`} handleSuccess={refetchDirects} currentLevel={user.Company.subscription_level} />
          )}
          </>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} width={1}>
          <Search handleSearchChange={handleSearchChange} sx={{minWidth:200, width: 270}} />
          <Stack direction="row" gap={2} sx={{ flex: '1' }}>
            <CheckboxFilter
              adminOnly
              name="includeInactive"
              onChange={handleFilterUpdate}
              checked={filters.includeInactive}
              label="Inactive"
            />
            {companyType === 'buyer' && (
              <CheckboxFilter
                name="acceptingOffers"
                onChange={handleFilterUpdate}
                checked={filters.acceptingOffers}
                label={`Open only (${activeCount})`}
                subscriptionTypes={['silver', 'gold']}
                tooltip="Premium feature"
              />
            )}
          </Stack>
        </Stack>
        <Divider />

        {isFetching && !loading ? 
        (
          <></>
        ) :
        (!isFetching || (isFetching && loading)) ?
        (
          <>
          {loading && (
            <Loader />
          )}

          {!loading && (
            <>
            <Stack gap={1} flex={1}>
              {(!directs?.length || directs?.length === 0) && (
                <NotFound
                    title={`No related ${labelName} found`}
                    caption={
                    search
                        ? 'Please try another search term'
                        : ''
                    }
                    wrapperSx={{ mt: 19 }}
                />
              )}

              {directs.map((direct) => (
                <RelatedDirectsListItem
                  key={direct.direct_id}
                  companyID={id}
                  companyType={companyType}
                  {...direct}
                />
              ))}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
        </>
          )}
        </>
        ) :
        (
          <></>
        )}
      </Stack>
    </>
  );

   function useRefetchDirects() {
    useEffect(() => {
      if (currentTab === directsTabName && (account.super_admin || companyType === 'buyer' || (user.Company.is_premium && user.Company.subscription_level !== 'free'))) {
        refetchDirects();
      }
    }, [page, pageSize, filters, search]);
  }

  function useTabReset() {
    useEffect(() => {
      if (currentTab !== directsTabName) {
        setPage(1);
        setSearch('');
        handleFilterUpdate({target: {name: "includeInactive", checked: false}});
        handleFilterUpdate({target: {name: "includeExpired", checked: includeExpired}});
      }
    }, [currentTab]);
  }
};
